import { inject, injectable } from 'inversify'
import type { BaseFilterInterface, BaseFilterPartialParams } from '@/core/tables/types'
import type { RegisteredServices } from '@/core/container/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type BaseFilterForm from '@/services/tables/filters/baseFilterForm'
import type {
  FilterServiceInterface,
  FormFieldFilterType,
  SelectOption,
  SelectValue,
  TpFormData,
} from '@/services/forms/types'
import type {
  PaginationUrlFilterScalarValueType,
  PaginationUrlFilterSingleType,
  ParsedFilterType,
} from '@/services/tables/types'
import { isPaginationUrlFilterScalarType, isPaginationUrlFilterScalarValueType } from '@/services/tables/types'
import type { Operation } from '@/services/types'
import { isBoolFilterOperation, isFilterOperation } from '@/services/types'
import { FILTER_RESERVED_NAMES, ALL_FILTER_OPTION, type FiltersToCreate } from '@/services/tables/filters/types'
import type TranslateService from '@/services/translateService'
import type { FilterField } from '@/services/tables/filters/fields/filterField'
import type FilterSchemaService from '@/services/filterSchemaService'
import type { FilterBuilderService } from '@/services/tables/filters/filterBuilderService'
import type LoggerService from '@/services/loggerService'
import { TmTableFilterError } from '@/core/error/table/tmTableFilterError'
import TmLogicError from '@/core/error/tmLogicError'
import type { PartialUIConfig } from '@/services/wrappers/types'
import type { FilterStrategyManager } from '@/services/tables/filters/strategies/filterStrategyManager'
import type { Nullable } from '@/types'
import { coersceBoolean } from '@/utils/boolean/coersceBoolean'
import type { FilterSelectedItems } from '@/data/models/domain/filteredViews/types'
import { isSameFilterValue } from '@/services/tables/filters/utils/valueComparison'
import { isEmpty, isPlainObject } from 'lodash-es'

const enum FilterObservabilityState {
  All,
  OnlyObservable,
  OnlyHidden,
}

@injectable()
export default abstract class BaseFilters<TName extends string = string, R = SelectValue>
  implements BaseFilterInterface, FilterServiceInterface<FormFieldFilterType, TName, R>
{
  protected _id: string | null = null

  protected form: BaseFilterForm<FormFieldFilterType>

  protected current: RegisteredServices

  protected openOnAdd = true

  private lastFieldOrder = 0

  constructor(
    @inject(SERVICE_TYPES.FilterSchemaService) protected readonly filterSchemaService: FilterSchemaService,
    @inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService,
    @inject(SERVICE_TYPES.FilterStrategyManager) protected readonly filterStrategyManager: FilterStrategyManager,
    @inject(SERVICE_TYPES.FilterBuilderService) protected readonly filterBuilderService: FilterBuilderService,
    @inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService,
  ) {}

  protected abstract getFilters(): FiltersToCreate<TName, R>

  public initForm(filters?: FiltersToCreate<TName, R>): BaseFilterForm<FormFieldFilterType> {
    this.form = this.filterBuilderService.build<FormFieldFilterType, TName, R>(this.id, filters ?? this.getFilters())

    return this.form
  }

  public setId(id: string) {
    this._id = id
  }

  public get id(): string {
    if (!this._id) {
      throw new TmLogicError(
        'No id is assigned to the filter service. Please set it using setId() method before using the service',
      )
    }

    return this._id
  }

  public getServiceId(): string {
    return this.id
  }

  public makeFieldVisible(filter: FormFieldFilterType): void {
    const filterName = filter.getName()
    if (this.openOnAdd) {
      this.setOpenOnAddFlag(filter.id, true)
    }

    this.setFieldVisibility(filterName, true)
  }

  public setOpenOnAddFlag(filterId: string, flag: boolean) {
    const filter = this.getForm().getFieldById(filterId)
    if (filter && filter.openOnAdd !== flag) {
      filter.patch({ openOnAdd: flag })
    }
  }

  public makeFilterReadOnly(filterName: string) {
    this.getForm().getFieldOrNull(filterName)?.patch({ readonly: true })
  }

  public resetField(filterName: string) {
    const field = this.getForm().getField(filterName)
    field.patch({
      value: '',
      innerValue: '',
      innerSelectedItemsValue: [],
      operation: field.initialOperation,
      innerOperation: field.initialOperation,
    })

    this.setFieldVisibility(filterName, false)
  }

  public getData(exportHidden = true) {
    const filters = this.getActiveFilters(exportHidden)

    return filters.reduce((query, filter) => {
      const value = filter.getApiValue()
      if (value === ALL_FILTER_OPTION) return query
      return {
        ...query,
        [filter.getName()]: value,
      }
    }, {})
  }

  public toQuery(exportHidden = true, filters: FormFieldFilterType[] | null = null): PaginationUrlFilterSingleType {
    if (!filters) {
      filters = this.getActiveFilters(exportHidden)
    }

    return filters.reduce<PaginationUrlFilterSingleType>((query, filter) => {
      if (filter.getApiValue() !== ALL_FILTER_OPTION) {
        const queryName = filter.getQueryName()

        // Filters sharing a query name fill different parts of it, so merge instead of overwriting
        return {
          ...query,
          [queryName]: { ...query[queryName], ...this.filterToQueryPart(filter) },
        }
      }

      return query
    }, {})
  }

  public getSelectedItems(exportHidden = true, filters: FormFieldFilterType[] | null = null): FilterSelectedItems[] {
    filters ??= this.getActiveFilters(exportHidden)

    return filters.reduce<FilterSelectedItems[]>((result, filter) => {
      const options = filter.selectedItemsValue
      if (Array.isArray(options)) {
        const filterName = filter.getName()
        const selectedItems: Array<Record<string, unknown>> = []
        for (const opt of options) {
          const entity = this.getEntityFromFilterOption(filterName, opt)
          if (entity) {
            selectedItems.push(entity)
          }
        }
        result.push({
          fieldName: filterName,
          items: selectedItems,
        })
      }

      return result
    }, [])
  }

  public isDefaultState(includeHiddenFilters = true) {
    const query = this.toQuery(includeHiddenFilters)
    return !Object.keys(query).length
  }

  public getActiveFilters(exportHidden = true): FormFieldFilterType[] {
    const form = this.getForm()

    if (!form) {
      return [] // on start when form is not init yet
    }

    return form.getFields().reduce((acc: FormFieldFilterType[], filter) => {
      // Check if filter has a value
      const hasValue = Array.isArray(filter.value)
        ? filter.value.length > 0
        : filter.value != null && filter.value !== ''

      // Only include if it has a value AND meets visibility criteria
      if (hasValue && (exportHidden || this.isObservableFilter(filter))) {
        acc.push(filter)
      }

      return acc
    }, [])
  }

  /**
   * @return Filters which are observable filters
   * (i.e. filters that can be visible for user or controlled by him)
   */
  public getObservableFilters(): FormFieldFilterType[] {
    return this.getFormFields(FilterObservabilityState.OnlyObservable)
  }

  public getVisibleFilters(): FormFieldFilterType[] {
    return this.getObservableFilters().filter((field) => this.isVisibleFilter(field.name))
  }

  /**
   * @return Filters that ARE NOT currently visible for user
   */
  public getInvisibleFilters(): FormFieldFilterType[] {
    return this.getObservableFilters().filter((field) => !this.isVisibleFilter(field.name))
  }

  public reset(toSkip: string[] = []) {
    this.getForm()
      .getFields()
      .filter((filter: FormFieldFilterType) => !toSkip.includes(filter.getName()))
      .forEach((filter: FormFieldFilterType) => {
        filter.patch({
          value: filter.initialValue,
          innerValue: filter.initialValue,
          operation: filter.initialOperation,
          innerSelectedItemsValue: [],
          isApplied: false,
        })
      })

    this.resetAllFilterVisibility()
  }

  public getLabelFromFilterOption(_filterName: string, _entity: Record<string, unknown>): string {
    return ''
  }

  public getEntityFromFilterOption(_filterName: string, _option: SelectOption): Nullable<Record<string, unknown>> {
    return null
  }

  public populate(payload: ParsedFilterType[], selectedItems: FilterSelectedItems[] = [], setInitial = true): void {
    // Performance optimization: Only reset all fields during initial load
    // When applying filters (setInitial = false), removalEntries in populateFromUrl handle clearing
    if (setInitial) {
      this.reset()
    }

    const fieldsState: Record<string, boolean> = {}
    const availableFilters = this.getForm().getFields()
    const shownFilters: Record<string, Nullable<Record<string, unknown>>> = {}

    availableFilters.forEach((item) => {
      let selectedItemsOptions: SelectOption[] = []
      const strategy = this.filterStrategyManager.getFilterStrategy(item)
      const filterName = item.getName()
      const found = selectedItems?.find((i) => i.fieldName === filterName)
      if (found) {
        selectedItemsOptions = found.items.map((entity: Record<string, unknown>) => ({
          text: this.getLabelFromFilterOption(filterName, entity),
          value: (entity as { id: string }).id.toString(),
        }))
      }
      const populateData = strategy.getPopulateData(item, payload, selectedItemsOptions)
      if (populateData) {
        // Show field only if its value differs from initial value
        const isInitialValue =
          isSameFilterValue(populateData.value, item.initialValue) && populateData.operation === item.initialOperation
        // For default filters, always show them regardless of whether value equals initialValue
        fieldsState[item.getName()] = item.isDefault || !isInitialValue
        shownFilters[item.getName()] = populateData
      } else {
        // When getPopulateData returns null (removal entry), hide the field and reset its value
        // This ensures removed filters don't retain old values that could reappear in toQuery()
        // For default filters, keep them visible but still clear their values
        if (item.isDefault) {
          fieldsState[item.getName()] = true
          shownFilters[item.getName()] = null
        } else {
          fieldsState[item.getName()] = false
        }

        item.patch({
          value: '',
          innerValue: '',
          operation: item.initialOperation,
          innerOperation: item.initialOperation,
          innerSelectedItemsValue: [],
          isApplied: false,
        })
      }
    })

    this.setFieldsVisibility(fieldsState)
    this.getForm().populate(shownFilters, setInitial)
  }

  public populateFromUrl(
    params: PaginationUrlFilterSingleType,
    selectedItems: FilterSelectedItems[] = [],
    setInitial = false,
  ) {
    const fieldNamesToPopulate = Object.keys(params)
    const fieldNamesToRemove = this.getAppliedFilters()
      .map((filter) => filter.getName())
      .filter((name) => !fieldNamesToPopulate.includes(name))

    // Create removal entries for fields that need to be cleared
    const removalEntries: ParsedFilterType[] = fieldNamesToRemove.map((name) => {
      const field = this.getForm().getField(name)
      return {
        name,
        operation: field.initialOperation,
        value: '',
        relatedField: '',
      }
    })

    // Combine populate and removal entries
    this.populate([...this.parseUrlFilter(params), ...removalEntries], selectedItems, setInitial)
  }

  public getForm(): BaseFilterForm<FormFieldFilterType> {
    return this.form
  }

  public isFilterChanged(filterName: string) {
    return this.getForm().getField(filterName).isChangedAndApplied()
  }

  /**
   * Filters that are set by user (filters which has the non-default value)
   * @return {FormFieldFilterType[]}
   */
  public getAppliedFilters(): FormFieldFilterType[] {
    return this.getObservableFilters().filter((filter) => {
      const { value, initialValue, operation, initialOperation } = filter

      const isOperationModified = operation !== initialOperation

      if (isBoolFilterOperation(filter.operation)) {
        const v = coersceBoolean(value, true, false)
        const iv = coersceBoolean(initialValue, true, false)
        return isOperationModified || v !== iv
      }

      let hasValue = false
      if (Array.isArray(value) || isPlainObject(value)) {
        hasValue = !isEmpty(value)
      } else {
        hasValue = value != null && value !== ''
      }

      return isOperationModified || (hasValue && !isSameFilterValue(value, initialValue))
    })
  }

  public setInnerSelectedItems(name: string, selectedItems: SelectOption[]) {
    this.getForm().getField(name).patch({
      innerSelectedItemsValue: selectedItems,
      isApplied: false,
    })
  }

  public setInnerValue(name: string, innerValue: unknown) {
    this.getForm().getField(name).patch({
      innerValue,
      isApplied: false,
    })
  }

  public setValue(name: string, innerValue: string) {
    this.getForm().getField(name).patch({
      innerValue,
      value: innerValue,
      isApplied: true,
    })
  }

  public setSelectedItems(name: string, selectedItemsValue: SelectOption[]) {
    this.getForm().getField(name).patch({
      selectedItemsValue,
      isApplied: true,
    })
  }

  public setInnerOperation(name: string, innerOperation: Operation) {
    const filter = this.getForm().getFieldOrNull(name)
    if (filter && filter.availableOperations && !filter.availableOperations.includes(innerOperation)) {
      throw new TmTableFilterError(
        `Operation "${innerOperation}" is not available. Available operations are "${filter.availableOperations.join(
          ', ',
        )}"`,
      )
    }

    this.getForm().getField(name).patch({
      innerOperation,
      isApplied: false,
    })
  }

  public applyFilterValue(name: string) {
    const field = this.getForm().getField(name)
    field.patch({
      value: field.innerValue,
      selectedItemsValue: field.innerSelectedItemsValue,
      operation: field.innerOperation,
      isApplied: true,
    })
  }

  protected getFormField(name: string) {
    return this.getForm().getField(name)
  }

  public getFilterInnerValue(name: string) {
    return this.getFormField(name).innerValue
  }

  public getFilterInnerSelectedItems(name: string) {
    return this.getFormField(name).innerSelectedItemsValue
  }

  public getFilterSelectedItems(name: string): SelectOption[] {
    return this.getFormField(name).selectedItemsValue
  }

  public getFilterValue<T>(name: string): T {
    return this.getFormField(name).value as T
  }

  public isVisibleFilter(name: string) {
    return this.isDefaultFilter(name) || this.getFieldVisibility(name)
  }

  public isDefaultFilter(name: string) {
    return this.getForm().getFieldOrNull(name)?.isDefault === true
  }

  /**
   * Hidden filters are filters that exists but can't be visible
   * or controlled directly by user in any way
   */
  public isHiddenFilter(filter: FormFieldFilterType): boolean
  public isHiddenFilter(name: string): boolean
  public isHiddenFilter(filter: string | FormFieldFilterType): boolean
  public isHiddenFilter(filter: string | FormFieldFilterType): boolean {
    // We only have the name
    if (typeof filter === 'string') {
      return this.getForm().getFieldOrNull(filter)?.isHidden === true
    }

    // We've already have the filter object
    return filter.isHidden
  }

  /**
   * That's the opposite to {@link isHiddenFilter} method.
   * Observable filters are filters that CAN be visible in FilterBar
   * and / or controlled by user from there
   */
  public isObservableFilter(filter: FormFieldFilterType): boolean
  public isObservableFilter(name: string): boolean
  public isObservableFilter(filter: string | FormFieldFilterType): boolean {
    return !this.isHiddenFilter(filter)
  }

  public setApplied(state: boolean) {
    for (const filter of this.getForm().getFields()) {
      filter.patch({ isApplied: state })
    }
  }

  public toIdFilter(ids: string[]): PaginationUrlFilterScalarValueType {
    const filterId = this.filterBuilderService.createField({ value: ids, relatedFieldName: '' })

    return this.filterToQueryPart(filterId)
  }

  public parseUrlFilter(condition: PaginationUrlFilterSingleType): ParsedFilterType[] {
    const result: ParsedFilterType[] = []

    FILTER_RESERVED_NAMES.forEach((name) => {
      // Delete reserved field names because it cannot be filters
      delete condition[name]
    })

    const mapNumToStr = (arr: unknown[]) => {
      return arr.map((item) => {
        if (typeof item === 'number') {
          return item.toString()
        }
        return item
      })
    }

    for (const fieldName in condition) {
      const fieldCondition = condition[fieldName]

      for (const key in fieldCondition) {
        const fieldConditionItem = { [key]: fieldCondition[key] } satisfies PaginationUrlFilterSingleType

        if (isPaginationUrlFilterScalarType(fieldConditionItem)) {
          const val = fieldConditionItem[key]

          if (val === null) {
            if (isFilterOperation(key)) {
              result.push({
                name: fieldName,
                operation: key,
                value: null,
              })
            }
          } else {
            Object.entries(val).forEach(([operation, value]) => {
              if (isFilterOperation(operation)) {
                result.push({
                  name: fieldName,
                  operation,
                  value: Array.isArray(value) ? mapNumToStr(value) : value,
                  relatedField: key,
                })
              }
            })
          }
        } else if (isPaginationUrlFilterScalarValueType(fieldConditionItem)) {
          const val = fieldConditionItem[key]
          if (isFilterOperation(key)) {
            result.push({
              name: fieldName,
              operation: key,
              value: Array.isArray(val) ? mapNumToStr(val) : val,
            })
          }
        }
      }
    }
    return result
  }

  public buildForm(): Promise<void> {
    throw new TmTableFilterError('This method should not be called directly')
  }

  public submit(data: TpFormData): Promise<any> {
    throw new TmTableFilterError('This method should not be called directly')
  }

  public filterToQueryPart(filter: FilterField) {
    return this.filterStrategyManager.getFilterStrategy(filter).toQueryPart(filter)
  }

  /**
   * @param {string} fieldName The name of filter field which state we want to know
   * @return {boolean} Actual visibility state. "true" is for visible, "false" is for hidden
   */
  public getFieldVisibility(fieldName: string): boolean {
    return this.getForm().getFieldOrNull(fieldName)?.isVisible === true
  }

  public getFieldOrder(): string[] {
    return this.getForm()
      .getFields()
      .filter((field) => field.isVisible)
      .toSorted((first, second) => first.order - second.order)
      .map((field) => field.getName())
  }

  public checkFilterValue(filter: FilterField, valueToCheck: unknown): boolean {
    if (!filter) {
      throw new TmTableFilterError('Filter not passed')
    }
    const strategy = this.filterStrategyManager.getFilterStrategy(filter)
    return strategy.checkFilterValue(filter, valueToCheck)
  }

  public checkFilterValueByName(filterName: string, valueToCheck: unknown): boolean {
    const filter = this.getFormFieldByName(filterName)
    if (!filter) {
      throw new TmTableFilterError(`Filter "${filterName}" not found`)
    }
    return this.checkFilterValue(filter, valueToCheck)
  }

  public getPartial(): PartialUIConfig<BaseFilterPartialParams> {
    return {
      name: `filter:${this.id}`,
      type: 'groupPartial',
      items: this.getForm()
        .getFields()
        .filter((field) => !field.isHidden)
        .map((field) => {
          return {
            name: field.name,
            params: {
              filterKey: this.id,
            },
          }
        }),
    }
  }

  public setFieldsVisibility(fields: Record<string, boolean>): void {
    const fieldsToHide: string[] = []
    const fieldsToShow: string[] = []

    for (const [fieldName, newState] of Object.entries(fields)) {
      const field = this.getForm().getFieldOrNull(fieldName)

      if (!field || field.isHidden || field.isVisible === newState) {
        continue
      }

      if (newState) {
        this.lastFieldOrder += 1
        field.patch({ isVisible: true, order: this.lastFieldOrder })
        fieldsToShow.push(fieldName)
      } else {
        field.patch({ isVisible: false })
        fieldsToHide.push(fieldName)
      }
    }

    if (!fieldsToHide.length && !fieldsToShow.length) {
      return
    }

    this.log(
      `Hide "${fieldsToHide.join(', ')}" filters and show "${fieldsToShow.join(', ')}" inside "${
        this.id
      }" filter service`,
    )
  }

  /**
   * @param {string} fieldName The name of filter field which needs to be set
   * @param {boolean} newState Visibility state which needs to be set for the field. "true" is visible, "false" is hidden
   * @protected
   */
  public setFieldVisibility(fieldName: string, newState: boolean): void {
    this.setFieldsVisibility({ [fieldName]: newState })
  }

  /** Reverts all filter field visibility back to defaults */
  public resetAllFilterVisibility(): void {
    this.log(`Reset filter visibility inside "${this.id}" filter service`)

    const fields = this.getForm().getFields()

    fields.forEach((field, index) => {
      field.patch({ isVisible: field.isDefault && !field.isHidden, order: index })
    })

    this.lastFieldOrder = fields.length
  }

  protected log(message: any, subchannel = 'baseFilters') {
    if (this.loggerService.shouldLogByChannel('filters', [subchannel])) {
      this.loggerService.log('filters', typeof message === 'string' ? message : JSON.stringify(message), subchannel)
    }
  }

  protected getFormFields(
    observability: FilterObservabilityState = FilterObservabilityState.All,
  ): FormFieldFilterType[] {
    const fields = this.getForm().getFields()

    if (observability === FilterObservabilityState.OnlyHidden) {
      return fields.filter((field) => field.isHidden)
    }

    if (observability === FilterObservabilityState.OnlyObservable) {
      return fields.filter((field) => !field.isHidden)
    }

    return fields
  }

  protected getFormFieldByName(name: string): FormFieldFilterType | undefined {
    return this.getForm().getFieldOrNull(name) ?? undefined
  }

  /**
   * Cleanup method called when filter service is destroyed.
   * Override this method in subclasses to implement custom cleanup logic
   * (e.g., unsubscribing from model subscriptions).
   */
  public destroy(): void {
    // Default implementation does nothing
    // Subclasses can override to add cleanup logic
  }
}
